import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  Badge,
  Form,
  Pagination,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaSearch } from 'react-icons/fa';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  listarTodosGruposEmpresa,
  listarPropostasMistas,
  listarImoveisMista,
  listarTitulosMista,
} from '~/Services/Service';
import FormCancelamento from './components/FormCancelamento';

const PAGE_SIZE = 20;

const formatarMoeda = (v) => {
  if (v == null || isNaN(Number(v))) return '-';
  return Number(v).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatarPercentual = (v) => {
  if (v == null || isNaN(Number(v))) return '-';
  return `${Number(v).toFixed(2).replace('.', ',')}%`;
};

const formatarData = (v) => {
  if (!v) return '-';
  const m = moment(v);
  return m.isValid() ? m.format('DD/MM/YYYY') : '-';
};

const TIPO_TITULO_LABEL = {
  CHE: 'Cheque',
  BOL: 'Boleto',
  CAR: 'Cartão',
  DEP: 'Depósito',
  DIN: 'Dinheiro',
};

const CancelamentoMista = () => {
  const [geOptions, setGeOptions] = useState([]);
  const [selectedGe, setSelectedGe] = useState(null);
  const [selectedConta, setSelectedConta] = useState(null);

  const [propostas, setPropostas] = useState([]);
  const [selectedProposta, setSelectedProposta] = useState(null);
  const [imoveis, setImoveis] = useState([]);
  const [titulos, setTitulos] = useState([]);

  const [loadingGes, setLoadingGes] = useState(false);
  const [loadingPropostas, setLoadingPropostas] = useState(false);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState(false);

  // Carrega GEs do usuário e auto-seleciona quando existe apenas 1
  useEffect(() => {
    let ativo = true;
    setLoadingGes(true);
    listarTodosGruposEmpresa()
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        const opts = lista.map((g) => ({
          value: g.codigo_grupo_empresa,
          label: `${g.codigo_grupo_empresa} - ${g.apelido || g.descricao || ''}`.trim(),
        }));
        setGeOptions(opts);
        if (opts.length === 1) {
          setSelectedGe(opts[0]);
        }
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
        setGeOptions([]);
      })
      .finally(() => {
        if (ativo) setLoadingGes(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Opções de conta derivadas das propostas carregadas (sem endpoint extra)
  const contasOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    propostas.forEach((p) => {
      if (p.conta_cedente == null) return;
      if (seen.has(p.conta_cedente)) return;
      seen.add(p.conta_cedente);
      opts.push({
        value: p.conta_cedente,
        label: `${p.conta_cedente} - ${p.apelido_conta || ''}`.trim(),
      });
    });
    return opts.sort((a, b) => a.value - b.value);
  }, [propostas]);

  // Filtro client-side por conta selecionada
  const propostasFiltradas = useMemo(() => {
    if (selectedConta == null) return propostas;
    return propostas.filter((p) => p.conta_cedente === selectedConta.value);
  }, [propostas, selectedConta]);

  const totalPaginas = Math.max(1, Math.ceil(propostasFiltradas.length / PAGE_SIZE));
  const propostasPagina = useMemo(() => {
    const start = (pagina - 1) * PAGE_SIZE;
    return propostasFiltradas.slice(start, start + PAGE_SIZE);
  }, [propostasFiltradas, pagina]);

  // Clamp da página quando a lista encolhe
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [totalPaginas, pagina]);

  // Carrega imóveis + títulos quando uma proposta é selecionada (anti-race com flag ativo)
  useEffect(() => {
    if (!selectedProposta) {
      setImoveis([]);
      setTitulos([]);
      return undefined;
    }
    let ativo = true;
    setLoadingDetalhes(true);
    Promise.all([
      listarImoveisMista(selectedProposta.proposta, selectedProposta.efe),
      listarTitulosMista(selectedProposta.proposta),
    ])
      .then(([resImoveis, resTitulos]) => {
        if (!ativo) return;
        setImoveis(Array.isArray(resImoveis) ? resImoveis : []);
        setTitulos(Array.isArray(resTitulos) ? resTitulos : []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
        setImoveis([]);
        setTitulos([]);
      })
      .finally(() => {
        if (ativo) setLoadingDetalhes(false);
      });
    return () => {
      ativo = false;
    };
  }, [selectedProposta]);

  const handleBuscar = useCallback(async () => {
    if (!selectedGe) {
      toast.warn('Selecione um Grupo Empresa antes de buscar.');
      return;
    }
    setLoadingPropostas(true);
    setBusca(true);
    setSelectedProposta(null);
    setPagina(1);
    try {
      const res = await listarPropostasMistas(
        selectedGe.value,
        selectedConta ? selectedConta.value : 0
      );
      setPropostas(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
      setPropostas([]);
    } finally {
      setLoadingPropostas(false);
    }
  }, [selectedGe, selectedConta]);

  const handleSelecionarProposta = (p) => {
    if (selectedProposta && selectedProposta.proposta === p.proposta) {
      setSelectedProposta(null);
      return;
    }
    setSelectedProposta(p);
  };

  // Atualiza a proposta na lista para EFE='S' após cancelamento bem-sucedido
  const handleCancelado = useCallback((codigoProposta, sequencia) => {
    setPropostas((prev) =>
      prev.map((p) =>
        p.proposta === codigoProposta ? { ...p, efe: 'S' } : p
      )
    );
    setSelectedProposta((prev) =>
      prev && prev.proposta === codigoProposta ? { ...prev, efe: 'S' } : prev
    );
    toast.success(
      sequencia
        ? `Cancelamento efetivado. Sequência: ${sequencia}`
        : 'Cancelamento efetivado com sucesso.'
    );
  }, []);

  const handlePagina = (pg) => {
    if (pg < 1 || pg > totalPaginas) return;
    setPagina(pg);
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const itens = [];
    const max = 5;
    let start = Math.max(1, pagina - Math.floor(max / 2));
    const end = Math.min(totalPaginas, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i += 1) {
      itens.push(
        <Pagination.Item
          key={`pag-${i}`}
          active={i === pagina}
          onClick={() => handlePagina(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return (
      <Pagination size="sm" className="mb-0">
        <Pagination.First onClick={() => handlePagina(1)} disabled={pagina === 1} />
        <Pagination.Prev onClick={() => handlePagina(pagina - 1)} disabled={pagina === 1} />
        {start > 1 && <Pagination.Ellipsis disabled />}
        {itens}
        {end < totalPaginas && <Pagination.Ellipsis disabled />}
        <Pagination.Next
          onClick={() => handlePagina(pagina + 1)}
          disabled={pagina === totalPaginas}
        />
        <Pagination.Last
          onClick={() => handlePagina(totalPaginas)}
          disabled={pagina === totalPaginas}
        />
      </Pagination>
    );
  };

  return (
    <>
      <Header title="Cancelar Proposta Mista" />
      <Container fluid className="py-3">
        <Card className="mb-3">
          <Card.Body>
            <Row className="align-items-end">
              <Col xs={12} md={5} className="mb-2 mb-md-0">
                <Form.Label className="small mb-1">Grupo Empresa</Form.Label>
                <Select
                  options={geOptions}
                  value={selectedGe}
                  onChange={(opt) => {
                    setSelectedGe(opt);
                    setSelectedConta(null);
                    setPropostas([]);
                    setSelectedProposta(null);
                    setBusca(false);
                  }}
                  isClearable
                  placeholder="Selecione..."
                  styles={selectStylesSm}
                  isDisabled={loadingGes || loadingPropostas}
                  isLoading={loadingGes}
                  noOptionsMessage={() => 'Nenhum GE disponível'}
                />
              </Col>
              <Col xs={12} md={5} className="mb-2 mb-md-0">
                <Form.Label className="small mb-1">Cliente</Form.Label>
                <Select
                  options={contasOptions}
                  value={selectedConta}
                  onChange={setSelectedConta}
                  isClearable
                  placeholder={
                    propostas.length === 0
                      ? 'Buscar primeiro para ver clientes'
                      : 'Todos'
                  }
                  styles={selectStylesSm}
                  isDisabled={loadingPropostas || propostas.length === 0}
                  noOptionsMessage={() => 'Nenhum cliente'}
                />
              </Col>
              <Col xs={12} md={2} className="d-flex justify-content-md-end">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleBuscar}
                  disabled={!selectedGe || loadingPropostas}
                  className="w-100"
                >
                  <FaSearch className="mr-1" />
                  Buscar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header className="py-2">
            <strong>Propostas Mistas</strong>
          </Card.Header>
          <Card.Body className="p-2">
            {loadingPropostas ? (
              <Loading />
            ) : (
              <>
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-2">
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Proposta</th>
                        <th>Cliente</th>
                        <th>Família</th>
                        <th style={{ textAlign: 'right' }}>Empreendimento</th>
                        <th style={{ width: 120 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {propostasPagina.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-3">
                            {busca
                              ? 'Nenhum registro encontrado!'
                              : 'Selecione um Grupo Empresa e clique em Buscar.'}
                          </td>
                        </tr>
                      )}
                      {propostasPagina.map((p) => {
                        const ativa =
                          selectedProposta && selectedProposta.proposta === p.proposta;
                        return (
                          <tr
                            key={`prop-${p.proposta}`}
                            onClick={() => handleSelecionarProposta(p)}
                            style={{
                              cursor: 'pointer',
                              backgroundColor: ativa ? '#e9f5ff' : undefined,
                            }}
                          >
                            <td>{p.proposta}</td>
                            <td>
                              {p.conta_cedente} - {p.apelido_conta || ''}
                            </td>
                            <td>
                              {p.familia} - {p.nome_da_familia || ''}
                            </td>
                            <td style={{ textAlign: 'right' }}>{p.projeto || '-'}</td>
                            <td>
                              {p.efe === 'S' ? (
                                <Badge variant="danger">Cancelado</Badge>
                              ) : (
                                <Badge variant="success">Ativa</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <div className="d-flex justify-content-between align-items-center px-1">
                  <small className="text-muted">
                    {propostasFiltradas.length > 0 &&
                      `Mostrando ${(pagina - 1) * PAGE_SIZE + 1} até ${Math.min(
                        pagina * PAGE_SIZE,
                        propostasFiltradas.length
                      )} de ${propostasFiltradas.length} registros`}
                  </small>
                  {renderPaginacao()}
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {selectedProposta && (
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <Card className="h-100">
                <Card.Header className="py-2 d-flex justify-content-between align-items-center">
                  <strong>Imóveis da Proposta {selectedProposta.proposta}</strong>
                  {selectedProposta.efe === 'S' && (
                    <Badge variant="secondary">Histórico</Badge>
                  )}
                </Card.Header>
                <Card.Body className="p-2">
                  {loadingDetalhes ? (
                    <Loading />
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Quadra</th>
                            <th>Lote</th>
                            <th style={{ textAlign: 'right' }}>Valor</th>
                            <th style={{ textAlign: 'right' }}>Boleto</th>
                            <th style={{ textAlign: 'right' }}>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {imoveis.length === 0 && (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-3">
                                Nenhum imóvel.
                              </td>
                            </tr>
                          )}
                          {imoveis.map((im) => (
                            <tr key={`imv-${im.seq}-${im.ativo}`}>
                              <td>{im.quadra || '-'}</td>
                              <td>{im.lote || '-'}</td>
                              <td style={{ textAlign: 'right' }}>
                                {formatarMoeda(im.valor)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {im.boleto || '-'}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {formatarPercentual(im.perc)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <Card className="h-100">
                <Card.Header className="py-2">
                  <strong>Títulos em Aberto</strong>
                </Card.Header>
                <Card.Body className="p-2">
                  {loadingDetalhes ? (
                    <Loading />
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Tipo</th>
                            <th>Vencimento</th>
                            <th style={{ textAlign: 'right' }}>Valor</th>
                            <th style={{ textAlign: 'right' }}>Portador</th>
                            <th>Baixado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {titulos.length === 0 && (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-3">
                                Nenhum título em aberto.
                              </td>
                            </tr>
                          )}
                          {titulos.map((t) => (
                            <tr key={`tit-${t.codigo_titulo}`}>
                              <td>{TIPO_TITULO_LABEL[t.tipo] || t.tipo}</td>
                              <td>{formatarData(t.data_vencimento)}</td>
                              <td style={{ textAlign: 'right' }}>
                                {formatarMoeda(t.valor)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {t.conta_portador || '-'}
                              </td>
                              <td>{t.baixado === 'S' ? 'Sim' : 'Não'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {selectedProposta && selectedProposta.efe === 'N' && (
          <Card className="mb-3">
            <Card.Header className="py-2">
              <strong>Cancelamento</strong>
            </Card.Header>
            <Card.Body>
              <FormCancelamento
                proposta={selectedProposta}
                codigoGe={selectedGe ? selectedGe.value : null}
                geLabel={selectedGe ? selectedGe.label : ''}
                onCancelado={handleCancelado}
              />
            </Card.Body>
          </Card>
        )}
      </Container>
    </>
  );
};

export default CancelamentoMista;
